import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Calendar, Activity, Sparkles, TrendingUp, MessageCircle } from "lucide-react";
import PortfolioHealthPreview from "./previews/PortfolioHealthPreview";
import AiAnalysisPreview from "./previews/AiAnalysisPreview";
import DividendCalendarPreview from "./previews/DividendCalendarPreview";

/**
 * Feature Bento — replaces the linear FeatureSpotlight with a denser
 * 12-column grid inspired by the reference. Each card embeds one of our
 * existing animated previews (kept intact, just reframed).
 */
const FeatureBento = () => {
  const { t } = useTranslation();

  return (
    <section id="features" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section heading */}
        <motion.div
          className="max-w-3xl mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="bento-eyebrow mb-4">{t("solution.badge")}</p>
          <h2
            className="font-extrabold leading-[1.05] tracking-[-0.03em] text-balance text-foreground"
            style={{ fontSize: "clamp(2.25rem, 4vw, 3.75rem)" }}
          >
            {t("solution.headline")}{" "}
            <span style={{ color: "hsl(var(--md-sage))" }}>
              {t("solution.headlineHighlight")}
            </span>
          </h2>
        </motion.div>

        {/* 12-col bento */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Card 1 — Dividend Calendar (span 7) */}
          <BentoCard delay={0} className="lg:col-span-7">
            <BentoHeader
              Icon={Calendar}
              tag={t("spotlight.dividends.eyebrow")}
              title={t("spotlight.dividends.title")}
              desc={t("spotlight.dividends.desc")}
            />
            <div className="mt-6">
              <DividendCalendarPreview />
            </div>
          </BentoCard>

          {/* Card 2 — Portfolio Health (span 5) */}
          <BentoCard delay={0.1} className="lg:col-span-5">
            <BentoHeader
              Icon={Activity}
              tag={t("spotlight.health.eyebrow")}
              title={t("spotlight.health.title")}
              desc={t("spotlight.health.desc")}
            />
            <div className="mt-6 flex justify-center">
              <PortfolioHealthPreview />
            </div>
          </BentoCard>

          {/* Card 3 — AI Analysis (span 8) */}
          <BentoCard delay={0.15} className="lg:col-span-8">
            <BentoHeader
              Icon={Sparkles}
              tag={t("spotlight.ai.eyebrow")}
              title={t("spotlight.ai.title")}
              desc={t("spotlight.ai.desc")}
            />
            <Link to="/terms#fair-use" className="text-sm text-primary hover:underline">{t("faq.fairUse")}</Link>
            <div className="mt-6">
              <AiAnalysisPreview />
            </div>
          </BentoCard>

          {/* Card 4 — Net worth widget (span 4) */}
          <BentoCard delay={0.2} className="lg:col-span-4">
            <BentoHeader
              Icon={TrendingUp}
              tag={t("solution.pillar1Title")}
              title="Your wealth, live."
              desc={t("solution.pillar1Desc")}
            />
            <div className="mt-8 relative">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Net worth
              </p>
              <p
                className="font-extrabold tabular-nums tracking-tight"
                style={{ fontSize: "2.5rem", color: "hsl(var(--md-text))" }}
              >
                $84,210
              </p>
              <p
                className="text-sm font-semibold mt-1"
                style={{ color: "hsl(var(--md-forest))" }}
              >
                ↑ +$1,240 this week
              </p>

              {/* Mini sparkline */}
              <svg viewBox="0 0 200 60" className="mt-6 w-full h-16" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--md-forest))" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="hsl(var(--md-forest))" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,42 L20,38 L40,40 L60,30 L80,32 L100,24 L120,26 L140,18 L160,22 L180,12 L200,10 L200,60 L0,60 Z"
                  fill="url(#spark-fill)"
                />
                <path
                  d="M0,42 L20,38 L40,40 L60,30 L80,32 L100,24 L120,26 L140,18 L160,22 L180,12 L200,10"
                  fill="none"
                  stroke="hsl(var(--md-sage))"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </BentoCard>
        </div>

        {/* AI Chat coming-soon teaser */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mt-12 rounded-3xl p-10 lg:p-14 overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--md-surface)) 0%, hsl(var(--md-bg)) 100%)",
            border: "1px solid hsl(var(--md-amber) / 0.18)",
          }}
        >
          <div
            className="absolute -top-32 -right-32 w-80 h-80 rounded-full blur-[120px]"
            style={{ background: "hsl(var(--md-amber) / 0.18)" }}
          />
          <div
            className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full blur-[120px]"
            style={{ background: "hsl(var(--md-sage) / 0.10)" }}
          />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                style={{ background: "hsl(var(--md-amber) / 0.18)" }}
              >
                <MessageCircle className="w-4 h-4" style={{ color: "hsl(var(--md-amber))" }} />
                <span
                  className="text-xs font-bold uppercase tracking-widest"
                  style={{ color: "hsl(var(--md-amber))" }}
                >
                  {t("spotlight.chat.eyebrow")}
                </span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-4 text-balance text-foreground">
                {t("spotlight.chat.title")}
              </h3>
              <p className="text-muted-foreground leading-relaxed">{t("spotlight.chat.desc")}</p>
            </div>

            <div className="space-y-3">
              {[
                { from: "you", text: t("spotlight.chat.q1") },
                { from: "ai", text: t("spotlight.chat.a1") },
                { from: "you", text: t("spotlight.chat.q2") },
              ].map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.15 }}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                    m.from === "you"
                      ? "ml-auto rounded-br-md"
                      : "rounded-bl-md"
                  }`}
                  style={
                    m.from === "you"
                      ? {
                          background: "hsl(var(--md-surface-2))",
                          color: "hsl(var(--md-text))",
                          border: "1px solid hsl(var(--md-sage) / 0.10)",
                        }
                      : {
                          background: "hsl(var(--md-sage))",
                          color: "hsl(var(--md-bg))",
                        }
                  }
                >
                  {m.text}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- helpers ---------------- */

type BentoCardProps = {
  className?: string;
  delay?: number;
  children: React.ReactNode;
};
const BentoCard = ({ className = "", delay = 0, children }: BentoCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    className={`bento-card p-7 lg:p-9 ${className}`}
  >
    <div className="relative z-10 h-full flex flex-col">{children}</div>
  </motion.div>
);

type BentoHeaderProps = {
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  tag: string;
  title: string;
  desc: string;
};
const BentoHeader = ({ Icon, tag, title, desc }: BentoHeaderProps) => (
  <>
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
      style={{ background: "hsl(var(--md-sage) / 0.10)" }}
    >
      <Icon className="w-5 h-5" style={{ color: "hsl(var(--md-sage))" }} />
    </div>
    <p className="bento-eyebrow mb-3">{tag}</p>
    <h3 className="text-xl lg:text-2xl font-extrabold tracking-tight mb-2.5 text-foreground">
      {title}
    </h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </>
);

export default FeatureBento;
